import { getAutomaticActivitySource } from "@/shared/api/activity-source";
import { useAuth } from "@/shared/auth/AuthProvider";
import { useEffect, useMemo, useRef } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  browserTimeZone,
  fetchCalendarStatus,
  hasGoogleTasksWriteScope,
  type CalendarStatus,
} from "@/shared/api/calendar";
import {
  calendarGrantCacheIdentity,
  calendarProviderCacheIdentity,
  refreshCalendarAgendaQueries,
} from "@/shared/calendar/calendar-agenda-cache";
import { isCurrentAccountCloudHydrated, onCloudSyncStatusChanged } from "@/shared/sync/cloud-sync";
import { pendingCurrentContentPushOptions } from "@/shared/sync/content-push-intent";
import {
  createTasksProviderSyncCoordinator,
  onGoogleTasksInboundReconcileRequested,
  pullGoogleTasksFromConnectedProvider,
} from "@/shared/tasks/google-tasks-sync";
import {
  captureGoogleIntegrationsRuntimeGeneration,
  isGoogleIntegrationsRuntimeGenerationCurrent,
  runWithGoogleIntegrationsRuntimeGeneration,
} from "@/shared/google/google-integration-runtime.mjs";

const FOREGROUND_SYNC_DEBOUNCE_MS = 150;
const PROVIDER_RETRY_BASE_MS = 2_000;
const PROVIDER_RETRY_MAX_MS = 60_000;

/**
 * Reconciles the dedicated Today's Brief Google Tasks mirror on app entry and
 * foreground edges. Provider-only lists are refreshed by
 * the agenda queries; the provider sync additionally writes remote mirror
 * completions/deletions into the account task collection before the normal
 * cloud pull updates every local Tasks consumer.
 */
export function useGoogleTasksForegroundSync(googleIntegrationsEnabled: boolean): void {
  const { signedIn } = useAuth();
  const queryClient = useQueryClient();
  const timeZone = useMemo(() => browserTimeZone(), []);
  const connectionRef = useRef<CalendarStatus | null>(null);
  const googleIntegrationRenderGeneration = googleIntegrationsEnabled
    ? captureGoogleIntegrationsRuntimeGeneration()
    : null;
  const { data: connection, refetch: refetchStatus } = useQuery({
    queryKey: ["calendar-status"],
    queryFn: ({ signal }) =>
      runWithGoogleIntegrationsRuntimeGeneration(googleIntegrationRenderGeneration, () =>
        fetchCalendarStatus({ signal, activitySource: getAutomaticActivitySource() }),
      ),
    enabled: signedIn && googleIntegrationsEnabled && googleIntegrationRenderGeneration !== null,
    refetchOnWindowFocus: true,
    retry: 1,
    staleTime: 60_000,
  });
  connectionRef.current = connection || null;

  const scheduleProviderSync = useMemo(
    () =>
      createTasksProviderSyncCoordinator(pullGoogleTasksFromConnectedProvider, () => {
        if (!isGoogleIntegrationsRuntimeGenerationCurrent(googleIntegrationRenderGeneration)) {
          return;
        }
        return refreshCalendarAgendaQueries(queryClient);
      }),
    [googleIntegrationRenderGeneration, queryClient],
  );
  const providerIdentity = calendarProviderCacheIdentity(connection);
  const grantIdentity = calendarGrantCacheIdentity(connection);
  const tasksWritable =
    signedIn && googleIntegrationsEnabled && hasGoogleTasksWriteScope(connection);

  useEffect(() => {
    if (
      !tasksWritable ||
      !providerIdentity ||
      !isGoogleIntegrationsRuntimeGenerationCurrent(googleIntegrationRenderGeneration)
    )
      return;
    let disposed = false;
    let timer: number | null = null;
    let providerRetryAttempt = 0;

    function pageCanReconcile() {
      if (!isGoogleIntegrationsRuntimeGenerationCurrent(googleIntegrationRenderGeneration)) {
        return false;
      }
      if (document.visibilityState === "hidden") return false;
      return typeof navigator === "undefined" || navigator.onLine !== false;
    }

    function scheduleProviderRetry() {
      if (disposed || timer !== null || !pageCanReconcile()) return;
      const delay = Math.min(
        PROVIDER_RETRY_MAX_MS,
        PROVIDER_RETRY_BASE_MS * 2 ** Math.min(providerRetryAttempt, 5),
      );
      providerRetryAttempt += 1;
      timer = window.setTimeout(() => {
        timer = null;
        void reconcile(true);
      }, delay);
    }

    async function reconcile(fetchFreshStatus: boolean) {
      if (disposed || !pageCanReconcile()) return;
      if (!isCurrentAccountCloudHydrated()) {
        scheduleProviderRetry();
        return;
      }
      if (pendingCurrentContentPushOptions().includeTasks === true) {
        scheduleProviderRetry();
        return;
      }
      let connection = connectionRef.current;
      if (fetchFreshStatus) {
        try {
          const freshStatus = await refetchStatus();
          if (disposed || !pageCanReconcile()) return;
          if (freshStatus.isError) {
            scheduleProviderRetry();
            return;
          }
          connection = freshStatus.data || null;
        } catch {
          scheduleProviderRetry();
          return;
        }
      }
      if (
        disposed ||
        pendingCurrentContentPushOptions().includeTasks === true ||
        !hasGoogleTasksWriteScope(connection) ||
        calendarProviderCacheIdentity(connection) !== providerIdentity ||
        calendarGrantCacheIdentity(connection) !== grantIdentity
      ) {
        return;
      }
      try {
        const result = await scheduleProviderSync({
          activitySource: getAutomaticActivitySource(),
          connection,
          googleIntegrationsEnabled,
          timeZone,
        });
        if (disposed || !pageCanReconcile()) return;
        if (result === "google-synced") {
          providerRetryAttempt = 0;
        } else {
          scheduleProviderRetry();
        }
      } catch {
        scheduleProviderRetry();
      }
    }

    function requestForegroundReconcile() {
      if (!pageCanReconcile()) return;
      providerRetryAttempt = 0;
      if (timer !== null) window.clearTimeout(timer);
      timer = window.setTimeout(() => {
        timer = null;
        void reconcile(true);
      }, FOREGROUND_SYNC_DEBOUNCE_MS);
    }

    void reconcile(false);
    const stopCloudSyncListener = onCloudSyncStatusChanged(() => {
      requestForegroundReconcile();
    });
    const stopReconcileRequestListener = onGoogleTasksInboundReconcileRequested(
      requestForegroundReconcile,
    );
    window.addEventListener("focus", requestForegroundReconcile);
    window.addEventListener("online", requestForegroundReconcile);
    document.addEventListener("visibilitychange", requestForegroundReconcile);
    return () => {
      disposed = true;
      stopCloudSyncListener();
      stopReconcileRequestListener();
      if (timer !== null) window.clearTimeout(timer);
      window.removeEventListener("focus", requestForegroundReconcile);
      window.removeEventListener("online", requestForegroundReconcile);
      document.removeEventListener("visibilitychange", requestForegroundReconcile);
    };
  }, [
    grantIdentity,
    googleIntegrationRenderGeneration,
    googleIntegrationsEnabled,
    providerIdentity,
    refetchStatus,
    scheduleProviderSync,
    tasksWritable,
    timeZone,
  ]);
}
